import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGE_STORAGE_KEY, Locale, Parameters, readLanguage, template, translate, translateOwnedText } from './language';
import { MessageKey } from './messages';
import './language.css';

export interface Language {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, values?: Parameters) => string;
  owned: (text: string) => string;
}
export interface LocalizationProps { i18n: Language }

const LanguageContext = createContext<Language>({
  locale: 'en', setLocale: () => undefined,
  t: (key, values) => translate('en', key, values), owned: text => text,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, updateLocale] = useState<Locale>(readLanguage);
  const language = useMemo<Language>(() => ({
    locale,
    setLocale: next => {
      try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next); } catch { /* Keep the in-memory choice. */ }
      updateLocale(next);
    },
    t: (key, values) => translate(locale, key, values),
    owned: text => translateOwnedText(locale, text),
  }), [locale]);

  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) updateLocale(readLanguage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return <LanguageContext.Provider value={language}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);

export function OwnedText({ text }: { text: string }) {
  const { owned } = useLanguage();
  return <>{owned(text)}</>;
}

/** Inject translation props without taking over a class component's existing contextType. */
export function withLanguage<P extends LocalizationProps>(Component: React.ComponentType<P>) {
  function LocalizedComponent(props: Omit<P, keyof LocalizationProps>) {
    const i18n = useLanguage();
    return <Component {...({ ...props, i18n } as P)} />;
  }
  LocalizedComponent.displayName = `withLanguage(${Component.displayName || Component.name || 'Component'})`;
  return LocalizedComponent;
}

/** Rich messages keep values and JSX intact and allow Japanese word order. */
export function Trans({ message, values = {} }: { message: MessageKey; values?: Record<string, React.ReactNode> }) {
  const { locale } = useLanguage();
  return <>{template(locale, message).split(/(\{\w+\})/g).map((part, index) =>
    <React.Fragment key={index}>{/^\{\w+\}$/.test(part) && Object.prototype.hasOwnProperty.call(values, part.slice(1, -1))
      ? values[part.slice(1, -1)] : part}</React.Fragment>)}</>;
}

export function LanguageToggle({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale } = useLanguage();
  return <div className={`language-toggle${compact ? ' language-toggle--compact' : ''}`} role="group" aria-label="Language / 言語">
    <button type="button" lang="en" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>English</button>
    <button type="button" lang="ja" aria-pressed={locale === 'ja'} onClick={() => setLocale('ja')}>日本語</button>
  </div>;
}
